import React, { Fragment, useEffect, useState } from 'react';
import {
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	// Accordion,
	AccordionSummary,
	AccordionDetails,
	styled as styledMui,
	Select,
	MenuItem,
	CircularProgress,
	Typography,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
} from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useMobile } from '../../../../hooks/use-mobile';
import disableRootElementScrollState from '../../../../atoms/disable-root-element-scroll-state';
import userState from '../../../../atoms/user-state';
import client from '../../../../clients/client';
import { formatNumberLocale, formatNumberWithCurrency } from '../../../../utils/data-utils';
import { AgreementCondition, ShippingZone } from '../../buy-tokens/reducer';
import { AgreementProduct } from '../../../../types';
import { Link } from 'react-router-dom';

const TabsContainer = styled(Box)`
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 0.5rem 0.2rem;
	margin-bottom: 1rem 0;
`;

const TabContainer = styled.div<{ active: boolean; isPlaceholderDiv?: boolean }>`
	display: flex;
	border-bottom: ${(props) => (props.active ? '2px solid #406AFF' : '1px solid #C6C6C666')};
	color: ${(props) => props.active && 'blue'};
	font-weight: ${(props) => (props.active ? '600' : '400')};
	flex-grow: ${(props) => (props.isPlaceholderDiv ? '1' : '0')};
`;

const TabDivider = styled.div<{ isLastDivider?: boolean }>`
	display: flex;
	border-bottom: 1px solid #c6c6c666;
	min-width: 2rem;
	flex-grow: ${(props) => (props.isLastDivider ? '1' : '0')};
`;

const Tab = styled.p<{ isPlaceholderP?: boolean }>`
	text-align: center;
	font-size: 16px;
	opacity: ${(props) => (props.isPlaceholderP ? '0' : '1')};
	cursor: ${(props) => (props.isPlaceholderP ? 'inherit' : 'pointer')};
	user-select: ${(props) => (props.isPlaceholderP ? 'none' : 'inherit')};
	width: ${(props) => (props.isPlaceholderP ? '100%' : 'auto')};
	font-family: 'Inter';
`;

const ColumnHeader = styled(TableCell)(({ theme }) => ({
	color: '#000000',
	fontWeight: 'bold',
	verticalAlign: 'middle',
}));

const StyledCell = styled(TableCell)(({ theme }) => ({
	color: 'grey.600',
	fontWeight: 400,
	textAlign: 'center',
	verticalAlign: 'middle',
}));

const Accordion = styledMui((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
	({ theme }) => ({
		borderBottom: `1px solid ${theme.palette.divider}`,
	})
);

interface Props {
	open: boolean;
	onClose: (event?: object | null, reason?: string) => void;
	agreementId?: string;
}

const DefaultParagraphTextConditionName = (props: { children: React.ReactNode | string }) => (
	<Typography className="font-semibold text-sm" color={'gray'}>
		{props.children}
	</Typography>
);

const DefaultParagraphTextConditionValue = (props: { children: React.ReactNode | string }) => (
	<Typography className="font-normal text-sm" color={'GrayText'}>
		{props.children}
	</Typography>
);

const NoCostParagraphText = (props: { children: React.ReactNode | string }) => (
	<Typography className="font-medium text-sm" color={'#47D27F'}>
		{props.children}
	</Typography>
);

const DefaultBoxGeneralContent = (props: {
	isLastItem: boolean;
	children: React.ReactNode;
}) => (
	<Box
		className={`flex justify-between items-center py-3 border-0 border-solid border-[#C6C6C6] ${
			props.isLastItem ? '' : 'border-b'
		}`}
	>
		{props.children}
	</Box>
);

interface MetadataKeyValue {
	key: string;
	value: number;
}

const termsByAgreement = {
	'/assets/terms_tokens.md': ['d9ac3a27', 'd9ac3a278', '14'],
	'/acopio/hierro/terms.md': ['15', '9', 'acd6c2c4'],
} as any;

const TermsAndConditionsDialog = (props: Props) => {
	const { open, onClose, agreementId } = props;
	const setDisableRootElementScroll = useSetRecoilState(disableRootElementScrollState);
	const user = useRecoilValue(userState);

	const intl = useIntl();
	const isMobile = useMobile();

	const [activeTab, setActiveTab] = useState(0);
	const [termsContent, setTermsContent] = useState('');
	const [agreementData, setAgreementData] = useState<any>({});
	const [loading, setLoading] = useState(true);

	const fetchAgreementData = async () => {
		setLoading(true);

		await client
			.getAgreementsConditionsAndEquivalences({ token: user.token, agreementId })
			.then((response) => {
				const financingMethods: AgreementCondition[] = []; // Financing methods are the conditions with condition_type_id = 4
				const shippings: AgreementCondition[] = []; // Shippings are the conditions with condition_type_id = 1
				let withdrawal: AgreementCondition | null = null; // Withdrawal is the condition with condition_type_id = 6

				// From the agreement and the metadata from the conditions, we need to get the min_purchase, min_redeem and min_time_to_ship
				// Initialize the arrays with the default value (key: '') and the value from the agreement
				const minPurchaseArray: MetadataKeyValue[] = [{ key: '', value: response.agreement.min_purchase }];
				const minRedeemArray: MetadataKeyValue[] = [{ key: '', value: response.agreement.min_redeem }];
				const minTimeToShipArray: MetadataKeyValue[] = [{ key: '', value: response.agreement.min_time_to_ship }];

				// Iterate over the conditions to get the financing methods, shippings and withdrawal
				// To fill the conditions arrays {financingMethods, shippings and withdrawal(only one element)}
				// And also to fill the arrays with the min_purchase, min_redeem and min_time_to_ship
				response.agreementConditions.forEach((condition: AgreementCondition) => {
					if (condition.status === 'active') {
						if (condition.condition_type_id === '0') {
							// for this condition config show the min_purchase, min_redeem and min_time_to_ship if they have a value
							// Set as the first element of each array, as the default value of the agreement
							const metadata = JSON.parse(condition.metadata);
							if (metadata?.min_purchase) {
								minPurchaseArray.shift();
								minPurchaseArray.push({ key: '', value: metadata.min_purchase });
							}
							if (metadata?.min_redeem) {
								minRedeemArray.shift();
								minRedeemArray.push({ key: '', value: metadata.min_redeem });
							}
							if (metadata?.min_time_to_ship) {
								minTimeToShipArray.shift();
								minTimeToShipArray.push({ key: '', value: metadata.min_time_to_ship });
							}
						}
						if (condition.condition_type_id === '4') {
							financingMethods.push(condition);
						}
						if (condition.condition_type_id === '1') {
							shippings.push(condition);
							const metadata = JSON.parse(condition.metadata);
							if (metadata?.min_purchase) {
								minPurchaseArray.push({ key: condition.name, value: metadata.min_purchase });
							}
							if (metadata?.min_redeem) {
								minRedeemArray.push({ key: condition.name, value: metadata.min_redeem });
							}
							if (metadata?.min_time_to_ship) {
								minTimeToShipArray.push({ key: condition.name, value: metadata.min_time_to_ship });
							}
						}
						if (condition.condition_type_id === '6') {
							withdrawal = condition;
							const metadata = JSON.parse(condition.metadata);
							if (metadata?.min_purchase) {
								minPurchaseArray.push({ key: condition.name, value: metadata.min_purchase });
							}
							if (metadata?.min_redeem) {
								minRedeemArray.push({ key: condition.name, value: metadata.min_redeem });
							}
							if (metadata?.min_time_to_ship) {
								minTimeToShipArray.push({ key: condition.name, value: metadata.min_time_to_ship });
							}
						}
					}
				});

				const deliveryZones: ShippingZone[] = [];
				// Iterate over the shippings to get the zones and add them to the deliveryZones array
				shippings.forEach((shipping) =>
					shipping.zones?.forEach((zone: ShippingZone) =>
						deliveryZones.push({ ...zone, agreement_condition_id: shipping.id })
					)
				);
				// Sort the deliveryZones array to render the zones in alphabetical order
				deliveryZones.sort((a: ShippingZone, b: ShippingZone) => {
					if (
						(a.zone_label < b.zone_label && a.zone_label.length < b.zone_label.length) ||
						(a.zone_label < b.zone_label && a.zone_label.length === b.zone_label.length)
					) {
						return -1;
					}
					if (
						(a.zone_label > b.zone_label && a.zone_label.length > b.zone_label.length) ||
						(a.zone_label > b.zone_label && a.zone_label.length === b.zone_label.length)
					) {
						return 1;
					}
					return 0;
				});
				// And add the zone/address of the withdrawal to the deliveryZones array as the last element
				if (withdrawal) {
					(withdrawal as AgreementCondition)?.zones?.forEach((zone: ShippingZone) => {
						deliveryZones.push({ ...zone, agreement_condition_id: withdrawal?.id as string });
					});
				}

				// Sort the financingMethods array to render the financing methods in alphabetical order
				financingMethods.sort((a: any, b: any) => {
					if (
						(a.name < b.name && a.name.length < b.name.length) ||
						(a.name < b.name && a.name.length === b.name.length)
					) {
						return -1;
					}
					if (
						(a.name > b.name && a.name.length > b.name.length) ||
						(a.name > b.name && a.name.length === b.name.length)
					) {
						return 1;
					}
					return 0;
				});

				// on the arrays from metadata, check if all the shippings and withdrawal are present on that array
				// if all the shippings and the withdrawal have a value, remove the element with the default value (key: ''). because it means that value is never used
				const filteredMinPurchase = minPurchaseArray.filter((minPurchase) =>
					shippings.length + (withdrawal ? 1 : 0) === minPurchaseArray.length - 1 ? !!minPurchase.key : true
				);
				const filteredMinRedeem = minRedeemArray.filter((minRedeem) =>
					shippings.length + (withdrawal ? 1 : 0) === minRedeemArray.length - 1 ? !!minRedeem.key : true
				);
				const filteredMinTimeToShip = minTimeToShipArray.filter((minTimeToShip) =>
					shippings.length + (withdrawal ? 1 : 0) === minTimeToShipArray.length - 1 ? !!minTimeToShip.key : true
				);

				setAgreementData({
					financingMethods,
					shippings,
					withdrawal,
					deliveryZones,
					products: response.agreement_products,
					unity: response.agreement.unity,
					labelUnitsInPresentation: response.agreement.label_units_in_presentation,
					buyAndExchange: {
						minPurchase: filteredMinPurchase,
						minRedeem: filteredMinRedeem,
						minTimeToShip: filteredMinTimeToShip,
					},
					price: response.agreementInfo?.price ?? 0,
					emptyCollections: response.emptyCollections,
				});
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	useEffect(() => {
		(async () => {
			const termsUrl = Object.keys(termsByAgreement)
				.map((key) => (termsByAgreement[key].includes(agreementId) ? key : null))
				.filter((el) => el)[0];

			if (!termsUrl) {
				return;
			}

			await axios.get(`${window.location.origin}${termsUrl}`).then((result) => {
				setTermsContent(result.data);
			});
		})();

		if (agreementId) {
			fetchAgreementData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [agreementId]);

	useEffect(() => {
		setDisableRootElementScroll(open);
	}, [open, setDisableRootElementScroll]);

	const renderContent = () => {
		switch (activeTab) {
			case 0:
				return agreementData.emptyCollections ? (
					<Box className="w-full flex justify-center items-center">
						<Typography className="font-bold">
							{intl.formatMessage({ id: 'terms_and_conditions_dialog.general_tab.no_collection_active' })}
						</Typography>
					</Box>
				) : (
					<>
						<Accordion className="rounded-none">
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								className="font-bold"
								aria-controls="pricing-content"
								id="pricing-header"
							>
								{intl.formatMessage({ id: 'terms_and_conditions_dialog.general_tab.pricing_section.title' })}
							</AccordionSummary>
							<AccordionDetails>
								<Box className="flex justify-between items-center">
									<DefaultParagraphTextConditionName>
										{intl.formatMessage({ id: 'terms_and_conditions_dialog.general_tab.pricing_section.left_content' })}
										{['15', '9', 'acd6c2c4'].includes(agreementId ?? '') && (
											<Box component={'span'} className="font-light">
												{intl.formatMessage(
													{ id: 'terms_and_conditions_dialog.general_tab.pricing_section.left_content_audiences_link' },
													{
														link: (
															<Link to="/audiences" className="underline text-blue-500">
																{intl.formatMessage({
																	id: 'terms_and_conditions_dialog.general_tab.pricing_section.left_content_audiences_link_text',
																})}
															</Link>
														),
													}
												)}
											</Box>
										)}
									</DefaultParagraphTextConditionName>
									<DefaultParagraphTextConditionValue>
										{intl.formatMessage(
											{ id: 'terms_and_conditions_dialog.general_tab.pricing_section.right_content' },
											{ tokenValue: formatNumberWithCurrency(agreementData.price) }
										)}
									</DefaultParagraphTextConditionValue>
								</Box>
							</AccordionDetails>
						</Accordion>
						<Accordion className="rounded-none">
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								className="font-bold"
								aria-controls="financing-content"
								id="financing-header"
							>
								{intl.formatMessage({ id: 'terms_and_conditions_dialog.general_tab.financing_section.title' })}
							</AccordionSummary>
							<AccordionDetails className="py-0 -mt-3">
								{agreementData.financingMethods?.map((financing: AgreementCondition, index: number) => (
									<DefaultBoxGeneralContent
										key={`termsAndConditions-financeOption${financing.id}`}
										isLastItem={index === agreementData.financingMethods.length - 1}
									>
										<DefaultParagraphTextConditionName>{financing.name}</DefaultParagraphTextConditionName>
										{!financing.value ? (
											<NoCostParagraphText>
												{intl.formatMessage({
													id: 'terms_and_conditions_dialog.general_tab.financing_section.no_cost',
												})}
											</NoCostParagraphText>
										) : financing.value_type === 'percentage' ? (
											<DefaultParagraphTextConditionValue>
												{intl.formatMessage(
													{ id: 'terms_and_conditions_dialog.general_tab.financing_section.cost_percentage_value' },
													{ value: formatNumberLocale(financing.value) }
												)}
											</DefaultParagraphTextConditionValue>
										) : (
											<DefaultParagraphTextConditionValue>
												{intl.formatMessage(
													{ id: 'terms_and_conditions_dialog.general_tab.financing_section.cost_fixed_value' },
													{ value: formatNumberWithCurrency(financing.value) }
												)}
											</DefaultParagraphTextConditionValue>
										)}
									</DefaultBoxGeneralContent>
								))}
							</AccordionDetails>
						</Accordion>
						<Accordion className="rounded-none">
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								className="font-bold"
								aria-controls="delivery-content"
								id="delivery-header"
							>
								{intl.formatMessage({ id: 'terms_and_conditions_dialog.general_tab.delivery_section.title' })}
							</AccordionSummary>
							<AccordionDetails>
								{agreementData.deliveryZones?.map((zone: ShippingZone, index: number) => {
									const condition =
										agreementData.shippings.find(
											(shipping: AgreementCondition) => shipping.id === zone.agreement_condition_id
										) ?? agreementData.withdrawal;
									return (
										<DefaultBoxGeneralContent
											key={`termsAndConditions-deliveryZone${zone.zone_id}-${index}`}
											isLastItem={index === agreementData.deliveryZones.length - 1}
										>
											{condition.condition_type_id === '6' ? (
												// Withdrawal
												<DefaultParagraphTextConditionName>
													{intl.formatMessage({
														id: 'terms_and_conditions_dialog.general_tab.delivery_section.withdrawal',
													})}
												</DefaultParagraphTextConditionName>
											) : (
												// Shipping
												<DefaultParagraphTextConditionName>{zone.zone_label}</DefaultParagraphTextConditionName>
											)}
											{condition.condition_type_id === '6' ? (
												// Withdrawal
												<DefaultParagraphTextConditionValue>{zone.zone_label}</DefaultParagraphTextConditionValue>
											) : !condition.value ? (
												<NoCostParagraphText>
													{intl.formatMessage({
														id: 'terms_and_conditions_dialog.general_tab.delivery_section.no_cost',
													})}
												</NoCostParagraphText>
											) : condition.value_type === 'percentage' ? (
												<DefaultParagraphTextConditionValue>
													{intl.formatMessage(
														{
															id: 'terms_and_conditions_dialog.general_tab.financing_section.cost_percentage_value',
														},
														{ value: formatNumberLocale(condition.value) }
													)}
												</DefaultParagraphTextConditionValue>
											) : (
												<DefaultParagraphTextConditionValue>
													{intl.formatMessage(
														{ id: 'terms_and_conditions_dialog.general_tab.financing_section.cost_fixed_value' },
														{ value: formatNumberWithCurrency(condition.value) }
													)}
												</DefaultParagraphTextConditionValue>
											)}
										</DefaultBoxGeneralContent>
									);
								})}
							</AccordionDetails>
						</Accordion>
						<Accordion className="rounded-none">
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								className="font-bold"
								aria-controls="buy-and-exchange-content"
								id="buy-and-exchange-header"
							>
								{intl.formatMessage({ id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.title' })}
							</AccordionSummary>
							<AccordionDetails>
								{agreementData.buyAndExchange?.minPurchase.map((minPurchase: MetadataKeyValue, index: number) => (
									<DefaultBoxGeneralContent
										key={`min-purchase-${index}-${minPurchase.key}`}
										isLastItem={false}
									>
										<DefaultParagraphTextConditionName>
											{!minPurchase.key
												? intl.formatMessage({
														id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.min_purchase',
												  })
												: intl.formatMessage(
														{
															id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.min_purchase_from_condition',
														},
														{ conditionName: <span className="font-extrabold">{minPurchase.key}</span> }
												  )}
										</DefaultParagraphTextConditionName>
										<DefaultParagraphTextConditionValue>
											{minPurchase.value > 1
												? intl.formatMessage(
														{ id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.tokens' },
														{ value: minPurchase.value }
												  )
												: intl.formatMessage({
														id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.token',
												  })}
										</DefaultParagraphTextConditionValue>
									</DefaultBoxGeneralContent>
								))}
								{agreementData.buyAndExchange?.minRedeem.map((minRedeem: MetadataKeyValue, index: number) => (
									<DefaultBoxGeneralContent
										key={`min-redeem-${index}-${minRedeem.key}`}
										isLastItem={false}
									>
										<DefaultParagraphTextConditionName>
											{!minRedeem.key
												? intl.formatMessage({
														id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.min_redeem',
												  })
												: intl.formatMessage(
														{
															id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.min_redeem_from_condition',
														},
														{ conditionName: <span className="font-extrabold">{minRedeem.key}</span> }
												  )}
										</DefaultParagraphTextConditionName>
										<DefaultParagraphTextConditionValue>
											{minRedeem.value > 1
												? `${minRedeem.value} ${agreementData.unity}s`
												: `${minRedeem.value} ${agreementData.unity}`}
										</DefaultParagraphTextConditionValue>
									</DefaultBoxGeneralContent>
								))}
								{agreementData.buyAndExchange?.minTimeToShip.map((minTimeToShip: MetadataKeyValue, index: number) => (
									<DefaultBoxGeneralContent
										key={`min-time-to-ship-${index}-${minTimeToShip.key}`}
										isLastItem={index === agreementData.buyAndExchange.minTimeToShip.length - 1}
									>
										<DefaultParagraphTextConditionName>
											{!minTimeToShip.key
												? intl.formatMessage({
														id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.min_time_to_ship',
												  })
												: intl.formatMessage(
														{
															id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.min_time_to_ship_from_condition',
														},
														{ conditionName: <span className="font-extrabold">{minTimeToShip.key}</span> }
												  )}
										</DefaultParagraphTextConditionName>
										<DefaultParagraphTextConditionValue>
											{minTimeToShip.value > 1
												? intl.formatMessage(
														{ id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.days' },
														{ value: minTimeToShip.value }
												  )
												: intl.formatMessage({
														id: 'terms_and_conditions_dialog.general_tab.buy_and_exchange_section.day',
												  })}
										</DefaultParagraphTextConditionValue>
									</DefaultBoxGeneralContent>
								))}
							</AccordionDetails>
						</Accordion>
					</>
				);
			case 1:
				return (
					<Table>
						<TableHead>
							<TableRow>
								<ColumnHeader colSpan={1} className="w-[50%] text-sm">
									{intl.formatMessage({ id: 'terms_and_conditions_dialog.equivalences_tab.product_column' })}
								</ColumnHeader>
								<ColumnHeader colSpan={1} className="w-[25%] text-center text-sm">
									{agreementData.labelUnitsInPresentation}
								</ColumnHeader>
								<ColumnHeader colSpan={1} className="w-[25%] text-center text-sm">
									{intl.formatMessage({ id: 'terms_and_conditions_dialog.equivalences_tab.equivalence_column' })}
								</ColumnHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{agreementData.products?.map((product: AgreementProduct) => (
								<TableRow key={`termsAndConditions-productPrice${product.id}`}>
									<StyledCell className={`w-[50%] text-left text-sm ${product.equivalence === 1 ? 'font-bold' : ''}`}>
										{product.name}
									</StyledCell>
									<StyledCell className={`w-[25%] text-center text-sm ${product.equivalence === 1 ? 'font-bold' : ''}`}>
										{`${formatNumberLocale(product.units_in_presentation)}${
											product.unit_type ? ` ${product.unit_type}` : ''
										}`}
									</StyledCell>
									<StyledCell className={`w-[25%] text-center text-sm ${product.equivalence === 1 ? 'font-bold' : ''}`}>
										{formatNumberLocale(product.equivalence)}
									</StyledCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				);
			case 2:
				return (
					<Box
						className="px-12 content"
						sx={{ fontFamily: 'Inter' }}
						dangerouslySetInnerHTML={{ __html: termsContent }}
					/>
				);
			default:
				return <Box className="px-12 content" dangerouslySetInnerHTML={{ __html: termsContent }} />;
		}
	};

	return (
		<Dialog
			aria-labelledby="terms-and-conditions-dialog"
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="lg"
			className="p-5"
			sx={{ '& .MuiPaper-root': { minHeight: 'calc(100% - 64px)', borderRadius: '6px' } }}
		>
			<DialogTitle className="text-2xl font-bold border-b-0">
				{intl.formatMessage({ id: 'terms_and_conditions_dialog.title' })}
				<IconButton aria-label="close" onClick={onClose} className="absolute right-5 top-5 text-gray-500">
					<CloseIcon />
				</IconButton>
				{isMobile ? (
					<Select
						disableUnderline
						labelId="select-tab-label"
						id="select-tab"
						value={activeTab}
						onChange={(event) => setActiveTab(event.target.value as number)}
						className="w-full mt-4"
						disabled={loading}
					>
						<MenuItem value={0}>{intl.formatMessage({ id: 'terms_and_conditions_dialog.general_tab.title' })}</MenuItem>
						<MenuItem value={1}>
							{intl.formatMessage({ id: 'terms_and_conditions_dialog.equivalences_tab.title' })}
						</MenuItem>
						<MenuItem value={2}>{intl.formatMessage({ id: 'terms_and_conditions_dialog.terms_tab.title' })}</MenuItem>
					</Select>
				) : (
					<TabsContainer>
						<TabContainer active={activeTab === 0}>
							<Tab onClick={() => !loading && setActiveTab(0)}>
								{intl.formatMessage({ id: 'terms_and_conditions_dialog.general_tab.title' })}
							</Tab>
						</TabContainer>
						<TabDivider>
							<Tab isPlaceholderP>{'|'}</Tab>
						</TabDivider>
						<TabContainer active={activeTab === 1}>
							<Tab onClick={() => !loading && setActiveTab(1)}>
								{intl.formatMessage({ id: 'terms_and_conditions_dialog.equivalences_tab.title' })}
							</Tab>
						</TabContainer>
						<TabDivider>
							<Tab isPlaceholderP>{'|'}</Tab>
						</TabDivider>
						<TabContainer active={activeTab === 2}>
							<Tab onClick={() => !loading && setActiveTab(2)}>
								{intl.formatMessage({ id: 'terms_and_conditions_dialog.terms_tab.title' })}
							</Tab>
						</TabContainer>
						<TabDivider isLastDivider>
							<Tab isPlaceholderP>{'|'}</Tab>
						</TabDivider>
					</TabsContainer>
				)}
			</DialogTitle>
			<DialogContent className={loading ? 'flex justify-center items-center' : ''}>
				{loading ? (
					<Box className="flex items-center justify-center h-full w-full">
						<CircularProgress color="inherit" />
					</Box>
				) : (
					renderContent()
				)}
			</DialogContent>
		</Dialog>
	);
};

export default TermsAndConditionsDialog;
